import { useEffect, useState, useMemo } from "react";

const EMPTY_LIST = [];
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Star, StarHalf, Users, Wrench, Mail, Phone,
  CheckCircle, Clock, XCircle, Award, MessageSquare,
} from "lucide-react";
import api from "../lib/api";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMedium } from "../lib/dateFormat";

/**
 * Feb 2026 · Partner-Profil-Seite (5 Sep 2026 · User-Directive):
 * „a real detail page listing their assignments, ratings and connected
 * customers in one place."
 *
 * Datenquelle: GET /api/team/partners/{id}/profile — ein Roundtrip
 * liefert Basisdaten, Assignments (desc), Stats, Ratings und die
 * distinkte Kunden-Kopplung. So bleibt die Seite auch bei vielen
 * Auftraegen fluessig.
 */
function StatusPill({ status }) {
  const map = {
    completed: { label: "Abgeschlossen", bg: BRAND_COLORS.greenDeep, icon: CheckCircle },
    checked_in: { label: "Vor Ort", bg: BRAND_COLORS.gold, icon: Clock },
    accepted: { label: "Angenommen", bg: BRAND_COLORS.navy, icon: Clock },
    assigned: { label: "Zugewiesen", bg: withAlpha(BRAND_COLORS.navy, 0.6), icon: Clock },
    declined: { label: "Abgelehnt", bg: "#8B1E1E", icon: XCircle },
    cancelled: { label: "Storniert", bg: withAlpha(BRAND_COLORS.navy, 0.3), icon: XCircle },
  };
  const m = map[status] || map.assigned;
  const Icon = m.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase px-2 py-0.5"
      style={{ background: m.bg, color: BRAND_COLORS.ivory }}
    >
      <Icon className="w-3 h-3" strokeWidth={1.8} /> {m.label}
    </span>
  );
}

function Stars({ n = 0, size = 12 }) {
  const filled = Math.round(n);
  const rest = 5 - filled;
  return (
    <span className="inline-flex items-center gap-0.5" data-testid="stars">
      {Array.from({ length: filled }).map((_, i) => (
        <Star key={`f${i}`} className="fill-current" width={size} height={size}
              style={{ color: BRAND_COLORS.gold }} strokeWidth={0} />
      ))}
      {Array.from({ length: rest }).map((_, i) => (
        <Star key={`e${i}`} width={size} height={size}
              style={{ color: withAlpha(BRAND_COLORS.gold, 0.35) }} strokeWidth={1.4} />
      ))}
    </span>
  );
}

export default function TeamPartnerDetail() {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  // Feb 2026 · Partner-Feedback-Filter (5 Sep 2026 · User-Directive):
  // erlaubt Admins den direkten Sprung zu „nur niedrige Bewertungen"
  // oder „nur dieses Jahr" ohne durch alle 150 Aufträge zu scrollen.
  const [filter, setFilter] = useState("all"); // all | low | this_year

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/team/partners/${id}/profile`);
        if (!alive) return;
        setState({ loading: false, data, error: null });
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, data: null, error: e.response?.data?.detail || e.message });
      }
    })();
    return () => { alive = false; };
  }, [id]);

  // Rules of Hooks: alle Hooks VOR jedem early return.
  const assignments = state.data?.assignments || EMPTY_LIST;
  const filteredAssignments = useMemo(() => {
    if (filter === "low") return assignments.filter((a) => a.rating && a.rating <= 3);
    if (filter === "this_year") {
      const currentYear = new Date().getFullYear();
      return assignments.filter((a) => (a.scheduled_date || "").startsWith(`${currentYear}-`));
    }
    return assignments;
  }, [assignments, filter]);
  const filterCounts = useMemo(() => ({
    all: assignments.length,
    low: assignments.filter((a) => a.rating && a.rating <= 3).length,
    this_year: assignments.filter((a) => (a.scheduled_date || "").startsWith(`${new Date().getFullYear()}-`)).length,
  }), [assignments]);

  if (state.loading) {
    return <div className="p-12 label-caps text-navy-dark/60" data-testid="partner-detail-loading">Wird geladen …</div>;
  }
  if (state.error) {
    return (
      <div className="p-12" data-testid="partner-detail-error">
        <Link to="/team/allowed" className="label-caps text-navy hover:text-gold inline-flex items-center gap-2 mb-4">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.6} /> Zurück
        </Link>
        <div className="bg-white border border-red-200 p-6 text-[13px] text-red-800">{String(state.error)}</div>
      </div>
    );
  }

  const { partner, stats, ratings, customers } = state.data;
  const fmtDate = (iso) => iso ? deDateMedium(iso) : "—";

  return (
    <div className="p-6 md:p-10 max-w-[1200px] mx-auto" data-testid="partner-detail-page">
      <Link
        to="/team/allowed"
        data-testid="partner-detail-back"
        className="label-caps text-navy-dark/70 hover:text-gold inline-flex items-center gap-2 mb-6"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.6} /> Zurück zu Zugängen
      </Link>

      {/* Header */}
      <div className="bg-white border border-navy/10 p-6 md:p-8 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="min-w-0">
            <div className="label-caps mb-2 flex items-center gap-2">
              <Wrench className="w-3 h-3 inline" strokeWidth={1.6} /> Partner
            </div>
            <h1
              className="font-display text-3xl md:text-4xl text-navy leading-tight truncate"
              data-testid="partner-detail-name"
            >
              {partner.name || partner.email}
            </h1>
            <div className="mt-2 flex flex-wrap gap-4 text-[13px] text-navy-dark/70">
              <span className="inline-flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" strokeWidth={1.6} /> {partner.email}
              </span>
              {partner.phone && (
                <span className="inline-flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" strokeWidth={1.6} /> {partner.phone}
                </span>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="label-caps mb-1 flex items-center justify-end gap-2">
              <Award className="w-3 h-3" strokeWidth={1.6} /> Bewertung
            </div>
            <div className="flex items-center gap-2 justify-end" data-testid="partner-overall-rating">
              <Stars n={ratings.overall_avg} size={16} />
              <span className="font-display text-xl text-navy">{ratings.overall_avg.toFixed(1)}</span>
            </div>
            <div className="text-[11px] text-navy-dark/60 mt-1">
              {ratings.count} Bewertung{ratings.count === 1 ? "" : "en"}
              {ratings.low_count > 0 && (
                <span className="ml-2 text-[#8B1E1E]">
                  · {ratings.low_count} niedrig
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stat-Chips */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { key: "total", label: "Gesamt", color: BRAND_COLORS.navy },
            { key: "completed", label: "Abgeschlossen", color: BRAND_COLORS.greenDeep },
            { key: "assigned", label: "Zugewiesen", color: withAlpha(BRAND_COLORS.navy, 0.6) },
            { key: "declined", label: "Abgelehnt", color: "#8B1E1E" },
            { key: "cancelled", label: "Storniert", color: withAlpha(BRAND_COLORS.navy, 0.3) },
          ].map((s) => (
            <div
              key={s.key}
              className="border border-navy/10 p-3"
              data-testid={`partner-stat-${s.key}`}
            >
              <div className="text-[10px] tracking-widest uppercase mb-1" style={{ color: s.color }}>{s.label}</div>
              <div className="font-display text-2xl text-navy">{stats[s.key] ?? 0}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Assignments */}
        <div className="lg:col-span-2 bg-white border border-navy/10 p-5 md:p-6" data-testid="partner-assignments-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="label-caps">Aufträge · {filteredAssignments.length}{filter !== "all" && <span className="text-navy-dark/45"> von {assignments.length}</span>}</h2>
            <div className="flex items-center gap-1 flex-wrap" data-testid="partner-assignment-filters">
              {[
                { k: "all", label: "Alle" },
                { k: "low", label: "≤ 3★" },
                { k: "this_year", label: "Dieses Jahr" },
              ].map((f) => (
                <button
                  key={f.k}
                  type="button"
                  onClick={() => setFilter(f.k)}
                  data-testid={`partner-filter-${f.k}`}
                  data-active={filter === f.k ? "1" : "0"}
                  className="px-2.5 py-1 text-[10.5px] tracking-widest uppercase border transition-colors"
                  style={{
                    background: filter === f.k ? BRAND_COLORS.navy : "transparent",
                    borderColor: filter === f.k ? BRAND_COLORS.navy : withAlpha(BRAND_COLORS.navy, 0.2),
                    color: filter === f.k ? BRAND_COLORS.ivory : BRAND_COLORS.navyDark,
                  }}
                >
                  {f.label}
                  <span
                    className="ml-1.5 opacity-70"
                  >
                    {filterCounts[f.k]}
                  </span>
                </button>
              ))}
            </div>
          </div>
          {filteredAssignments.length === 0 ? (
            <p className="text-[13px] text-navy-dark/60 italic" data-testid="partner-assignments-empty">
              {filter === "all" ? "Noch keine Aufträge für diesen Partner." : "Keine Aufträge im gewählten Filter."}
            </p>
          ) : (
            <ul className="divide-y divide-navy/10" data-testid="partner-assignments-list">
              {filteredAssignments.map((a) => (
                <li key={a.id} className="py-3.5" data-testid={`partner-assignment-${a.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-[14px] text-navy-dark font-medium truncate">
                        {a.title || "—"}
                      </div>
                      <div className="text-[12px] text-navy-dark/60 mt-0.5">
                        {a.customer_name || "—"}
                        {a.property_name && (
                          <>
                            <span className="opacity-40 mx-1">·</span>
                            {a.property_name}
                          </>
                        )}
                      </div>
                      <div className="text-[11.5px] text-navy-dark/50 mt-1 flex flex-wrap gap-2">
                        <span>{fmtDate(a.scheduled_date)}</span>
                        {a.kind && <span className="uppercase tracking-widest">· {a.kind}</span>}
                        {a.hours != null && <span>· {a.hours}h</span>}
                      </div>
                      {a.rating && (
                        <div className="mt-2 flex items-center gap-2">
                          <Stars n={a.rating} size={11} />
                          {a.rating_comment && (
                            <span className="text-[11.5px] text-navy-dark/70 italic truncate">
                              „{a.rating_comment}"
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0">
                      <StatusPill status={a.status} />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Right column: Customers + Ratings by kind */}
        <div className="space-y-6">
          <div className="bg-white border border-navy/10 p-5 md:p-6" data-testid="partner-customers-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="label-caps flex items-center gap-2">
                <Users className="w-3 h-3" strokeWidth={1.6} /> Verknüpfte Kunden · {customers.length}
              </h2>
            </div>
            {customers.length === 0 ? (
              <p className="text-[13px] text-navy-dark/60 italic" data-testid="partner-customers-empty">
                Noch keine Kunden-Kopplung.
              </p>
            ) : (
              <ul className="divide-y divide-navy/10" data-testid="partner-customers-list">
                {customers.map((c) => (
                  <li key={c.id} className="py-2.5" data-testid={`partner-customer-${c.id}`}>
                    <Link
                      to={`/team/customers/${c.id}`}
                      className="flex items-center justify-between gap-3 hover:text-gold group"
                    >
                      <div className="min-w-0">
                        <div className="text-[13.5px] text-navy-dark group-hover:text-gold font-medium truncate">
                          {c.name || c.email}
                        </div>
                        <div className="text-[11.5px] text-navy-dark/55 truncate">
                          {c.assignment_count} Auftr{c.assignment_count === 1 ? "ag" : "äge"}
                          {c.last_assignment_at && (
                            <span className="opacity-60"> · zuletzt {fmtDate(c.last_assignment_at)}</span>
                          )}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white border border-navy/10 p-5 md:p-6" data-testid="partner-ratings-by-kind-card">
            <h2 className="label-caps mb-4">Bewertung nach Art</h2>
            {ratings.by_kind.length === 0 ? (
              <p className="text-[13px] text-navy-dark/60 italic">Noch keine Bewertungen.</p>
            ) : (
              <ul className="space-y-3">
                {ratings.by_kind.map((k) => (
                  <li key={k.kind} className="flex items-center justify-between" data-testid={`partner-kind-${k.kind}`}>
                    <div className="text-[12px] uppercase tracking-widest text-navy-dark/70">{k.kind}</div>
                    <div className="flex items-center gap-2">
                      <Stars n={k.avg} size={11} />
                      <span className="text-[12px] text-navy">{k.avg.toFixed(1)}</span>
                      <span className="text-[11px] text-navy-dark/50">({k.count})</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {ratings.recent_comments.length > 0 && (
            <div className="bg-white border border-navy/10 p-5 md:p-6" data-testid="partner-recent-comments-card">
              <h2 className="label-caps mb-4 flex items-center gap-2">
                <MessageSquare className="w-3 h-3" strokeWidth={1.6} /> Kunden-Feedback
              </h2>
              <ul className="space-y-4">
                {ratings.recent_comments.map((c) => (
                  <li key={c.assignment_id} className="border-l-2 pl-3" style={{ borderColor: withAlpha(BRAND_COLORS.gold, 0.5) }}>
                    <div className="flex items-center gap-2 mb-1">
                      <Stars n={c.stars} size={10} />
                      <span className="text-[11px] text-navy-dark/60">{c.customer_name}</span>
                    </div>
                    <div className="text-[13px] text-navy-dark italic leading-relaxed">
                      „{c.comment}"
                    </div>
                    <div className="text-[10.5px] text-navy-dark/45 mt-1 tracking-widest uppercase">
                      {c.title} · {fmtDate(c.rated_at)}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
